import { useState, useEffect } from 'react';
import { enrollClaimMdProvider, fetchPayers } from '../services/api';
import './ClaimMdActions.css';

const toArray = (v) => (v == null ? [] : Array.isArray(v) ? v : [v]);

// Claim.MD enroll_type values (labels per the API's enrollment reference).
const ENROLL_TYPES = [
  { value: 'era',    label: 'ERA / 835 (Remittance)' },
  { value: '1500',   label: 'Professional Claims (837P / CMS-1500)' },
  { value: 'ub',     label: 'Institutional Claims (837I / UB-04)' },
  { value: 'elig',   label: 'Eligibility (270/271)' },
  { value: 'attach', label: 'Electronic Attachments' },
];

function readError(err, fallback) {
  const body = err?.response?.data;
  const detailErr = body?.details?.error;
  const payerMsg = Array.isArray(detailErr)
    ? detailErr.map((e) => e?.error_mesg).filter(Boolean).join('; ')
    : detailErr?.error_mesg;
  return payerMsg || body?.message || body?.error || err?.message || fallback;
}

export default function EnrollModal({ provider, onClose }) {
  const [form, setForm] = useState({
    payerid:     '',
    payerName:   '',
    enrollType:  'era',
    provTaxid:   '',
    provNpi:     provider?.npi || '',
    provName:    provider?.name || '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
  });

  const [payers, setPayers]               = useState([]);
  const [payerSearch, setPayerSearch]     = useState('');
  const [dropdownOpen, setDropdownOpen]   = useState(false);
  const [phase, setPhase]   = useState('form'); // 'form' | 'result'
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [link, setLink]     = useState(null);

  useEffect(() => {
    fetchPayers().then((res) => setPayers(res.data || [])).catch(() => {});
  }, []);

  const upd = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const filteredPayers = payers.filter((p) =>
    !payerSearch.trim() ||
    p.name.toLowerCase().includes(payerSearch.toLowerCase()) ||
    (p.payer_code || '').toLowerCase().includes(payerSearch.toLowerCase())
  );

  const selectPayer = (p) => {
    setForm((f) => ({ ...f, payerid: p.payer_code || '', payerName: p.name }));
    setPayerSearch('');
    setDropdownOpen(false);
  };

  const clearPayer = () => setForm((f) => ({ ...f, payerid: '', payerName: '' }));

  const handleSubmit = async () => {
    const missing = [];
    if (!form.payerid)   missing.push('Payer ID');
    if (!form.enrollType) missing.push('Enrollment type');
    if (!form.provTaxid) missing.push('Provider Tax ID');
    if (!form.provNpi && !form.provName) missing.push('Provider NPI or name');
    if (missing.length > 0) {
      setError('Missing required field(s): ' + missing.join(', '));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await enrollClaimMdProvider({
        payerid:    form.payerid.trim(),
        enrollType: form.enrollType,
        provTaxid:  form.provTaxid.replace(/\D/g, ''),
        provNpi:    form.provNpi.replace(/\D/g, '') || undefined,
        provNameL:  form.provName.trim() || undefined,
        contact:    form.contactName.trim() || undefined,
        extra: {
          contact_email: form.contactEmail.trim() || undefined,
          contact_phone: form.contactPhone.trim() || undefined,
        },
      });
      const url = toArray(res?.enrollment?.link)[0]?.url || res?.enrollment?.url || null;
      if (!url) {
        setError('Claim.MD did not return an enrollment link. Please try again.');
        setLoading(false);
        return;
      }
      setLink(url);
      setPhase('result');
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setError(readError(err, 'Failed to start enrollment. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cml-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <div className="cml-modal">
        <div className="cml-header">
          <div className="cml-header-left">
            <div className="cml-header-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
              </svg>
            </div>
            <div>
              <h2 className="cml-title">Payer Enrollment</h2>
              <p className="cml-subtitle">{provider?.name || 'Provider'}</p>
            </div>
          </div>
          <button className="cml-close" onClick={onClose} disabled={loading}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="cml-body">
          {phase === 'form' ? (
            <>
              <p className="cml-hint">
                Enroll this provider with a payer for a given transaction type. Claim.MD returns
                a hosted link with the enrollment form, quick-enroll option, or current status.
              </p>

              <div className="cml-section-label">Payer &amp; Type</div>
              <div className="cml-form-grid">
                <div className="cml-field cml-span-2">
                  <label className="cml-label">Payer <span className="cml-req">*</span></label>
                  {form.payerid || form.payerName ? (
                    <div className="cml-selected-row">
                      <span className="cml-selected-name">
                        {form.payerName || '—'}{form.payerid ? ` (${form.payerid})` : ''}
                      </span>
                      <button type="button" className="cml-selected-change" onClick={clearPayer}>Change</button>
                    </div>
                  ) : (
                    <div className="cml-combobox">
                      <input
                        className="cml-input"
                        value={payerSearch}
                        placeholder="Search payers…"
                        autoComplete="off"
                        onFocus={() => setDropdownOpen(true)}
                        onChange={(e) => { setPayerSearch(e.target.value); setDropdownOpen(true); }}
                        onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                      />
                      {dropdownOpen && (
                        <div className="cml-dropdown">
                          {filteredPayers.length === 0 ? (
                            <div className="cml-dropdown-empty">No payers match your search.</div>
                          ) : (
                            filteredPayers.slice(0, 30).map((p) => (
                              <button
                                key={p.payer_id}
                                type="button"
                                className="cml-dropdown-item"
                                onMouseDown={() => selectPayer(p)}
                                disabled={!p.payer_code}
                                title={p.payer_code ? '' : 'This payer has no Claim.MD payer ID on file'}
                              >
                                {p.name}
                                <span className="cml-dropdown-code">{p.payer_code || 'no payer ID'}</span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="cml-field cml-span-2">
                  <label className="cml-label">Enrollment Type <span className="cml-req">*</span></label>
                  <select className="cml-input" value={form.enrollType} onChange={upd('enrollType')}>
                    {ENROLL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="cml-section-label">Provider</div>
              <div className="cml-form-grid">
                <div className="cml-field cml-span-2">
                  <label className="cml-label">Provider / Org Name</label>
                  <input className="cml-input" value={form.provName} onChange={upd('provName')} />
                </div>
                <div className="cml-field">
                  <label className="cml-label">NPI</label>
                  <input className="cml-input" value={form.provNpi} onChange={upd('provNpi')} placeholder="10-digit NPI" />
                </div>
                <div className="cml-field">
                  <label className="cml-label">Tax ID <span className="cml-req">*</span></label>
                  <input className="cml-input" value={form.provTaxid} onChange={upd('provTaxid')} />
                </div>
              </div>

              <div className="cml-section-label">Contact (recommended)</div>
              <div className="cml-form-grid">
                <div className="cml-field cml-span-2">
                  <label className="cml-label">Name</label>
                  <input className="cml-input" value={form.contactName} onChange={upd('contactName')} />
                </div>
                <div className="cml-field">
                  <label className="cml-label">Email</label>
                  <input className="cml-input" type="email" value={form.contactEmail} onChange={upd('contactEmail')} />
                </div>
                <div className="cml-field">
                  <label className="cml-label">Phone</label>
                  <input className="cml-input" type="tel" value={form.contactPhone} onChange={upd('contactPhone')} />
                </div>
              </div>

              {error && <div className="cml-error">{error}</div>}
            </>
          ) : (
            <div className="cml-result">
              <div className="cml-result-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <p className="cml-result-title">Enrollment started</p>
              <p className="cml-result-sub">
                The enrollment form opened in a new tab. If it was blocked, use the button below.
              </p>
              <div className="cml-result-link">{link}</div>
            </div>
          )}
        </div>

        <div className="cml-footer">
          {phase === 'form' ? (
            <>
              <button className="cml-btn-cancel" onClick={onClose} disabled={loading}>Cancel</button>
              <button className="cml-btn-primary" onClick={handleSubmit} disabled={loading}>
                {loading ? (<><span className="cml-spinner" />Starting…</>) : 'Start Enrollment'}
              </button>
            </>
          ) : (
            <>
              <a className="cml-btn-primary" href={link} target="_blank" rel="noopener noreferrer">Open Enrollment Form</a>
              <button className="cml-btn-cancel" onClick={onClose}>Done</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
